import { ApiConnector } from '@/api/api.connector';
import type { IRequestListApi } from '@/pages/LeasingScoring/types/requests.types';
import type {
  IIdentityDocumentApi,
  ILeasingInfoApi,
  ILeasingRequestHistoryApi,
  IRequestChangeHistoryApi
} from '@/pages/LeasingScoringRequest/types/request.types';
import type { IRefreshCompanyInfoRequestApi } from '@/pages/LeasingScoringRequest/types/response.types';
import type { ECompanyPropertyDocumentField } from '@/shared/constants/leased-property';
import type {
  EDownPaymentMethod,
  ELeasingRequestFileTypes
} from '@/shared/constants/leasing';
import type {
  EExternalServiceCode,
  IExternalServiceCardApi,
  IScoringInfoApi
} from '@/shared/types/scoring-info.types';

class LeasingScoringApi extends ApiConnector {
  getRequests(params: IGetRequestsParams): Promise<IRequestListApi> {
    return this.call({ path: 'requests', params });
  }

  getRequestInfo(id: string): Promise<ILeasingInfoApi> {
    return this.call({ path: `requests/${id}` });
  }

  getIdentityDocument(requestId: string): Promise<IIdentityDocumentApi> {
    return this.call({ path: `requests/${requestId}/identity-document` });
  }

  getRequestLogs(
    id: string,
    abortSignal?: AbortSignal
  ): Promise<ILeasingRequestHistoryApi[]> {
    return this.call({
      path: `flash_logs/history/${id}`,
      abortSignal
    });
  }

  getRequestHistory(requestId: string): Promise<IRequestChangeHistoryApi> {
    return this.call({ path: `requests/${requestId}/history` });
  }

  // Запрос на получение скоринговой информации по пользователю к заявке
  getScoringInfo(decisionId: string): Promise<IScoringInfoApi> {
    return this.call({ path: `underwriter/scoring-info/${decisionId}` });
  }

  getExternalServices(requestId: string): Promise<IExternalServiceCardApi[]> {
    return this.call({ path: `requests/${requestId}/external-services` });
  }

  retryExternalService(
    requestId: string,
    service: EExternalServiceCode
  ): Promise<IExternalServiceCardApi[]> {
    return this.call({
      path: `requests/${requestId}/external-services/${service}/retry`,
      method: 'post'
    });
  }

  refreshCompanyInfoRequest(
    requestUuid: string
  ): Promise<IRefreshCompanyInfoRequestApi> {
    return this.call({
      path: `business-clients/requests/${requestUuid}/refresh`,
      method: 'post'
    });
  }

  approveRequest(decisionId: string): Promise<unknown> {
    return this.call({
      path: `underwriter/approve/${decisionId}`,
      method: 'post'
    });
  }

  rejectRequest(
    decisionId: string,
    body: IRejectRequestBody
  ): Promise<unknown> {
    return this.call({
      path: `underwriter/reject/${decisionId}`,
      method: 'post',
      body
    });
  }

  takeToWork(requestId: string): Promise<unknown> {
    return this.call({
      path: `requests/${requestId}/takeRequest`,
      method: 'post'
    });
  }

  // Ручное принятие, если имущество со склада
  acceptDecisionByClient(decisionId: string): Promise<unknown> {
    return this.call({
      path: `decisions/accept/${decisionId}`,
      method: 'post'
    });
  }

  approveForFinancing(requestId: string): Promise<unknown> {
    return this.call({
      path: `requests/${requestId}/approveForFinancing`,
      method: 'post'
    });
  }

  completeFinancing(requestId: string, body: { file: File }): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', body.file);

    return this.call({
      path: `requests/${requestId}/completeFinancing`,
      method: 'post',
      body: formData
    });
  }

  setDownPaymentMethod(
    contractId: string,
    method: EDownPaymentMethod
  ): Promise<unknown> {
    return this.call({
      path: `payments/contracts/${contractId}/down-payment/method`,
      method: 'put',
      body: { method }
    });
  }

  cancelDownPaymentMethod(contractId: string): Promise<unknown> {
    return this.call({
      path: `payments/contracts/${contractId}/down-payment/method`,
      method: 'delete'
    });
  }

  confirmCashDownPayment(contractId: string): Promise<unknown> {
    return this.call({
      path: `payments/contracts/${contractId}/down-payment/confirm`,
      method: 'post'
    });
  }

  confirmManualDownPayment(contractId: string, file: File): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', file);

    return this.call({
      path: `payments/contracts/${contractId}/down-payment/manual`,
      method: 'post',
      body: formData
    });
  }

  uploadDownPaymentInvoice(contractId: string, file: File): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', file);

    return this.call({
      path: `payments/contracts/${contractId}/down-payment-invoice`,
      method: 'post',
      body: formData
    });
  }

  uploadCompanyPropertyDocuments(
    companyPropertyId: string,
    documents: IUploadCompanyPropertyDocumentBody[]
  ): Promise<unknown> {
    const formData = new FormData();

    documents.forEach(({ documentType, file }) => {
      formData.append(documentType, file);
    });

    return this.call({
      path: `company-property/${companyPropertyId}/documents`,
      method: 'post',
      body: formData
    });
  }

  addKaskoInsurance(companyPropertyId: string): Promise<unknown> {
    return this.call({
      path: `company-property/${companyPropertyId}/kasko-insurance`,
      method: 'post'
    });
  }

  addKaskoInsuranceManual(
    companyPropertyId: string,
    body: IAddKaskoInsuranceManualBody
  ): Promise<unknown> {
    return this.call({
      path: `company-property/${companyPropertyId}/kasko-insurance/manual`,
      method: 'post',
      body
    });
  }

  addTelemetry(propertyId: string, body: IAddTelemetryBody): Promise<unknown> {
    const formData = new FormData();
    formData.append('isTelemetryInstalled', 'true');
    formData.append('telemetry_imei', body.telemetryImei);
    formData.append('telemetry_device_id', body.telemetryDeviceId);
    formData.append('telemetry_provider_name', body.telemetryProviderName);
    formData.append('telematics_contract', body.contractScan);
    return this.call({
      path: `company-property/${propertyId}/add-telemetry`,
      method: 'patch',
      body: formData
    });
  }

  finishRequest(requestId: string): Promise<unknown> {
    return this.call({
      path: `requests/${requestId}/leaseHandover`,
      method: 'post'
    });
  }

  sendHandoverDocuments(requestId: string): Promise<unknown> {
    return this.call({
      path: `requests/${requestId}/sendHandoverDocuments`,
      method: 'post'
    });
  }

  // ----- Create request -----

  getClientByPersonalId(
    personalId: string,
    abortSignal?: AbortSignal
  ): Promise<{
    personal_id: string;
    phone_number: string;
    last_name: string;
    first_name: string;
    middle_name: string;
  }> {
    return this.call({
      path: `clients/personal-ids/${personalId}`,
      abortSignal
    });
  }

  createOtp(body: ICreateOtpBody): Promise<{ request_uuid: string }> {
    return this.call({
      path: `requests/createOtp`,
      method: 'post',
      body
    });
  }

  validateOtp(
    body: IValidateOtpBody
  ): Promise<{ result: boolean; message?: string }> {
    return this.call({
      path: `requests/validateOtp`,
      method: 'post',
      body
    });
  }

  retryOtp(body: IRetryOtpBody): Promise<unknown> {
    return this.call({
      path: `requests/retryOtp`,
      method: 'post',
      body
    });
  }

  uploadRequestFile(body: {
    requestUuid: string;
    personalId: string;
    file: File;
  }): Promise<{ file: { id: string } }> {
    const formData = new FormData();
    formData.append('file', body.file);
    formData.append('clientIin', body.personalId);
    formData.append('requestUuid', body.requestUuid);
    return this.call({
      path: `requests/files/upload`,
      method: 'post',
      body: formData
    });
  }

  createRequest(body: ICreateRequestBody): Promise<unknown> {
    return this.call({
      path: `requests/submit`,
      method: 'post',
      body
    });
  }
}

export const leasingScoringApi = new LeasingScoringApi();

interface IGetRequestsParams {
  limit: number;
  offset: number;
  searchTerm?: string;
  cityId?: string;
  statusId?: string;
  dateFrom?: string;
  dateTo?: string;
  partnerId?: string;
  productType?: string;
  clientType?: string;
}

interface IRejectRequestBody {
  comment: string;
}

interface IAddTelemetryBody {
  telemetryImei: string;
  telemetryDeviceId: string;
  telemetryProviderName: string;
  contractScan: File;
}

interface IAddKaskoInsuranceManualBody {
  insurance_number: string;
  insurance_date_beg: string;
  insurance_date_end: string;
}

interface ICreateOtpBody {
  personalId: string;
  firstName: string;
  lastName: string;
  middleName: string;
  clientPhone: string;
}

interface IValidateOtpBody {
  requestUuid: string;
  otp: string;
}

interface IRetryOtpBody {
  requestUuid: string;
  clientPhone: string;
  type: 'WhatsApp' | 'SMS';
}

interface ICreateRequestBody {
  requestUuid: string;
  companyPropertyUuid: string;
  downPaymentAmount: number;
  loanAmount: number;
  loanPeriod: number;
  repaymentTypeCode: number;
  birthDate: string;
  maritalStatusCode: number;
  childCount: number;
  titleCompany: string;
  workAddress: string;
  salary: number;
  additionalIncome: number;
  additionalContact: {
    fullName: string;
    phoneNumber: string;
    relationType: number;
  };
  files: Array<{ id: string; type: ELeasingRequestFileTypes }>;
}

interface IUploadCompanyPropertyDocumentBody {
  file: File;
  documentType: ECompanyPropertyDocumentField;
}
